/**
 * Plain words for the product's internals (docs/admin-analytics-brief.md, "Plain words, no
 * internals"): an action name becomes what a person did, a route pattern becomes the screen's
 * name, a device class becomes a device. Anything unknown gets a readable fallback rather than
 * an id on screen.
 */

const featureWords: Record<string, string> = {
  'pages.upload_started': 'Uploaded pages',
  'pages.upload_finished': 'Finished an upload',
  'pages.added': 'Added pages',
  'scan.started': 'Scanned',
  'scan.finished': 'Finished a scan',
  'people.invite_sent': 'Invited someone',
  'people.invite_accepted': 'Accepted an invite',
  'api_key.created': 'Created an API key',
  'export.configured': 'Set up an export',
  'export.delivered': 'Export delivered',
  'review.opened': 'Opened a review',
  'review.confirmed': 'Confirmed a document',
  'review.corrected': 'Corrected a field',
  'review.type_changed': 'Changed a document type',
  'review.boundary_changed': 'Moved a document boundary',
  'review.withdrawn': 'Withdrew a confirmation',
  'review.page_removed': 'Removed a page',
  'review.page_restored': 'Restored a page',
  'review.documents_deleted': 'Deleted documents',
  'review.documents_restored': 'Restored deleted documents',
  'source.email_copied': 'Copied the email address',
  'source.folder_chosen': 'Chose a folder',
  'source.switched': 'Switched a source on or off',
  'source.senders_saved': 'Saved the allowed senders',
  'pages.folder_chosen': 'Chose a folder',
  'pages.upload_failed': 'An upload failed',
  'scan.failed': 'A scan failed',
  'scan.agent_found': 'Found Paige Scan',
  'scan.agent_missing': 'Looked for Paige Scan and did not find it',
  'scan.driver_window': "Chose the scanner's own window",
  'scan.scanner_settings': "Opened the scanner's settings",
  'export.removed': 'Removed an export',
  'export.failed': 'An export failed',
  'api_key.revoked': 'Revoked an API key',
  'company.closed': 'Closed the company',
  'company.renamed': 'Renamed the company',
  'people.jobs_narrowed': 'Narrowed someone to certain jobs',
  'people.removed': 'Removed someone',
  'people.role_changed': "Changed someone's role",
  'people.signed_out_everywhere': 'Signed someone out everywhere',
  'people.second_factor_reset': "Reset someone's two-step sign-in",
  'notifications.preference_changed': 'Changed a notification',
  'job.state_changed': 'Switched a job',
  'job.archived': 'Archived a job',
  'job.renamed': 'Renamed a job',
  'job.deleted': 'Deleted a job',
  'account.two_step_enrolled': 'Turned on two-step sign-in',
  'pricing.plan_chosen': 'Chose a plan',
  'pricing.talk_requested': 'Asked to talk to sales',
  'billing.held': 'A payment was held',
  'screen.viewed': 'Opened a screen',
  'screen.left': 'Left a screen',
  'session.started': 'Started a session',
  'request.failed': 'Was refused',
  'browser.error': 'Saw an error',
  'billing.checkout_started': 'Started checkout',
  'billing.plan_viewed': 'Looked at the plans',
  'billing.plan_chosen': 'Chose a plan',
  'billing.started': 'Started a plan',
  'billing.switched': 'Switched plan',
  'billing.renewed': 'Renewed',
  'billing.cancelled': 'Cancelled a plan',
  'billing.topup': 'Bought pages',
  'job.created': 'Created a job',
  'company.second_factor_on': 'Turned on two-step sign-in',
};

const screenWords: Record<string, string> = {
  '/': 'Home',
  '/pricing': 'Pricing',
  '/sign-up': 'Sign-up',
  '/sign-in': 'Sign-in',
  '/developers': 'Developer docs',
  '/t/$tenant': 'Dashboard',
  '/t/$tenant/jobs/new': 'New job',
  '/t/$tenant/jobs/$job/review': 'Review workbench',
  '/t/$tenant/jobs/$job/sources': 'Sources',
  '/t/$tenant/package': 'Plans',
  '/t/$tenant/settings': 'Settings',
  '/t/$tenant/settings/billing': 'Billing',
  '/t/$tenant/settings/company': 'Company',
  '/t/$tenant/settings/developers': 'Developers',
  '/t/$tenant/settings/people': 'People',
  '/t/$tenant/settings/company/general': 'Company, General',
  '/t/$tenant/settings/company/people': 'People',
  '/t/$tenant/settings/company/security': 'Security',
  '/t/$tenant/settings/company/documents': 'Documents',
  '/t/$tenant/settings/company/activity': 'Activity',
  '/t/$tenant/settings/billing/plan': 'Plan and pages',
  '/t/$tenant/settings/billing/alerts': 'Page alerts',
  '/t/$tenant/settings/billing/usage': 'Usage',
  '/t/$tenant/settings/billing/partner': 'Partner',
  '/t/$tenant/settings/developers/keys': 'API keys',
  '/t/$tenant/settings/developers/send': 'Send from your systems',
  '/t/$tenant/settings/developers/scan': 'Paige Scan',
};

const deviceWords: Record<string, string> = {
  phone: 'Phone',
  tablet: 'Tablet',
  desktop: 'Desktop',
  unknown: 'Unknown',
};

function sentence(raw: string): string {
  const words = raw.replace(/[._/$]+/g, ' ').trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Unknown';
}

/** "pages.upload_started" → "Uploaded pages"; an unknown name reads as its words. */
export function featureWord(feature: string): string {
  return featureWords[feature] ?? sentence(feature);
}

/** "/t/$tenant/jobs/$job/review" → "Review workbench"; an unknown pattern reads as its words. */
export function screenWord(screen: string | null | undefined): string {
  if (!screen) return 'No screen';
  // The router names the index route with a trailing slash ("/t/$tenant/"); it is the same screen.
  const key = screen.length > 1 ? screen.replace(/\/$/, '') : screen;
  return screenWords[key] ?? sentence(key.replace(/^\/t\/\$tenant/, '').replace(/\$[a-z]+/gi, ''));
}

export function deviceWord(device: string): string {
  return deviceWords[device] ?? sentence(device);
}

/** A session's walk as one line: "Dashboard → New job → Review workbench". */
export function pathWords(screens: readonly string[]): string {
  return screens.map(screenWord).join(' → ');
}
